import { bootstrap } from '$app/bootstrap';
import type { AppContext } from '$app/bootstrap';
import { catchesReducer } from '$domain/reducers/catches';
import type { CatchesState } from '$domain/reducers/catches';
import { settingsReducer } from '$domain/reducers/settings';
import type { Settings } from '$domain/reducers/settings';
import { plansReducer } from '$domain/plans';
import type { PlansState } from '$domain/plans';
import { onboardingReducer } from '$domain/reducers/onboarding';
import type { OnboardingState } from '$domain/reducers/onboarding';
import { reduceAll } from '$domain/reducers/reduce';

/**
 * The bridge between the event log and the screen.
 *
 * Two rules shape it:
 *
 * 1. **Derived state is never persisted as authoritative** (architecture §2.4).
 *    Everything here is recomputed from the log; nothing here is written back.
 * 2. **Nothing on the Catch screen waits for it.** Spec §15 wants the Catch
 *    button tappable in under a second, and architecture §2.7 makes that
 *    precise: tappable *before any store read completes*. So `ready` starts
 *    false, the button renders anyway, and the first tap is queued against a
 *    context that resolves a beat later.
 */

interface Derived {
  catches: CatchesState;
  settings: Settings;
  plans: PlansState;
  onboarding: OnboardingState;
}

const EMPTY: Derived = {
  catches: catchesReducer.initial,
  settings: settingsReducer.initial,
  plans: plansReducer.initial,
  onboarding: onboardingReducer.initial,
};

class AppState {
  ctx = $state<AppContext | null>(null);
  derived = $state<Derived>(EMPTY);
  /** Set when the store could not be opened at all — private mode, no quota. */
  storeError = $state<Error | null>(null);

  private booting: Promise<AppContext> | null = null;

  /**
   * Resolve the app context, starting the boot if it has not started.
   *
   * Awaited by the *action*, not by the render. A user who taps "Caught it" the
   * instant the app paints waits for IndexedDB for a few milliseconds; a user
   * who reads the screen first waits for nothing at all. Either way the button
   * was there immediately, which is the property that matters.
   */
  async context(): Promise<AppContext> {
    if (this.ctx) return this.ctx;
    this.booting ??= bootstrap().then(async (ctx) => {
      // Replay *before* publishing the context, so `ctx` and `derived` become
      // visible in the same tick. Assigning `ctx` first opened a window in which
      // the app looked booted while its derived state was still empty — and an
      // effect that re-ran inside that window read "onboarding not completed"
      // about a user who had completed it, and sent them back through the flow.
      await this.replay(ctx);
      this.ctx = ctx;
      return ctx;
    });
    try {
      return await this.booting;
    } catch (error) {
      this.booting = null;
      this.storeError = error instanceof Error ? error : new Error(String(error));
      throw error;
    }
  }

  /** Replay the log. A few milliseconds at this data volume (architecture §2.3). */
  async refresh(): Promise<void> {
    const ctx = this.ctx;
    if (!ctx) return;
    await this.replay(ctx);
  }

  private async replay(ctx: AppContext): Promise<void> {
    const events = await ctx.store.getAll();
    this.derived = reduceAll(
      {
        catches: catchesReducer,
        settings: settingsReducer,
        plans: plansReducer,
        onboarding: onboardingReducer,
      },
      events,
    );
  }
}

export const app = new AppState();

/**
 * Start the boot without blocking anything.
 *
 * Called once from the shell after mount, so the store is usually warm by the
 * time a tap arrives — an optimisation, never a precondition.
 */
export function warmUp(): void {
  void app.context().catch(() => {
    /* surfaced through app.storeError */
  });
}
